/* eslint-disable react-hooks/rules-of-hooks -- pre-existing violations unmasked by #1772; tracked + to be fixed in #1842. Do not add new conditional hooks here. */
import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  ReactNode,
} from 'react'
import { Snackbar, Alert } from '@mui/material'
import { useQuery } from '@tanstack/react-query'
import { useAuth0 } from '@/utils/auth/auth0'
import {
  useCurrentUser,
  useHandleAccountLinking,
  useUpdateUserMutation,
} from '../../utils/hooks/users'
import { User, UserAPIResponse } from '../../models/Users'
import { isOrgCreationDisabled } from '../../utils/utils'
import { clearDatabaseSearchStorage } from '@/utils/databaseSearchSession'
import { fetchUserOrgs } from '@/api/usersAPI'
import { UUID } from 'crypto'
import TermsAndConditionsBanner from '@/components/TermsAndConditionsBanner'
import LoginConflictMessage from '@/pages/LoginConflictMessage'
import AccountLinkingErrorModal from '@/components/AccountLinkingErrorModal'
import AccountLinkingSuccessModal from '@/components/AccountLinkingSuccessModal'
// Create the context
const UserContext = createContext<{
  user: User | null
  token: string | null
  orgs: UUID[] | null
  isError: boolean
  isLoading: boolean
  hasAgreedToTerms: boolean
  acceptTerms: () => void
  getToken: () => Promise<string>
  updateUser: ReturnType<typeof useUpdateUserMutation> | null
  // Org-switch error message. Lives here rather than in SelectOrg so it
  // survives SelectOrg unmounting (closing the mobile nav mid-switch) the
  // same way updateUser itself does - see SelectOrg.tsx for the mechanism.
  switchError: string | null
  setSwitchError: (message: string | null) => void
}>({
  user: null,
  token: null,
  orgs: null,
  isError: false,
  isLoading: true,
  hasAgreedToTerms: false,
  acceptTerms: () => {},
  getToken: () => Promise.resolve(''),
  updateUser: null,
  switchError: null,
  setSwitchError: () => {},
})

interface UserProviderProps {
  children: ReactNode
}

export const useUserOrgs = (
  token: string | null,
  currentUser: UserAPIResponse | null,
) => {
  const { logout, getAccessTokenSilently } = useAuth0()
  const userUuid = currentUser?.user_uuid ?? null

  const query = useQuery({
    queryKey: ['userOrgs', userUuid],
    queryFn: async () => {
      const freshToken = await getAccessTokenSilently()
      const result = await fetchUserOrgs(freshToken, userUuid as UUID)
      return result as UUID[]
    },
    enabled: !!token && !!userUuid,
  })

  // Logout if user has no orgs and org creation is disabled
  useEffect(() => {
    if (
      query.data &&
      Array.isArray(query.data) &&
      query.data.length === 0 &&
      isOrgCreationDisabled()
    ) {
      clearDatabaseSearchStorage()
      logout({ returnTo: window.location.origin })
    }
  }, [query.data, logout])

  return {
    orgs: Array.isArray(query.data) ? query.data : null,
    org_error: query.error as Error | null,
    isLoading: query.isLoading,
  }
}

export const UserProvider: React.FC<UserProviderProps> = ({ children }) => {
  const {
    isAuthenticated,
    getAccessTokenSilently,
    isLoading: authLoading,
    logout,
    user: auth0User,
  } = useAuth0()
  const logOutAndClear = useCallback(
    (options?: Parameters<typeof logout>[0]) => {
      clearDatabaseSearchStorage()
      return logout(options)
    },
    [logout],
  )
  const [token, setToken] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [showTermsModal, setShowTermsModal] = useState(false)
  const [showLinkingErrorModal, setShowLinkingErrorModal] = useState(false)
  const [showLinkingSuccessModal, setShowLinkingSuccessModal] = useState(false)
  const [displayErrorMessage, setDisplayErrorMessage] = useState<string | null>(
    null,
  )

  const { linkStatus, errorMessage, completeAccountLinking } =
    useHandleAccountLinking()

  // handles the account linking results
  useEffect(() => {
    if (linkStatus === 'success') {
      setShowLinkingSuccessModal(true)
    } else if (linkStatus === 'error') {
      // map to more user-friendly messages
      const errorMessagesMap: { [key: string]: string } = {
        'Email Mismatch':
          "The email addresses you're trying to link do not match. Only accounts with the same email address can be linked.",
        'Account Already Registered':
          'This account is already registered as a separate user and cannot be linked.',
        'Missing Email Data':
          'Unable to verify email addresses for account linking. Please try again.',
        'Emails do not match — cannot link these accounts':
          "The email addresses you're trying to link do not match. Only accounts with the same email address can be linked.",
        'Missing email information for linking':
          'Unable to verify email addresses for account linking. Please try again.',
      }

      const shouldShowModal = Object.keys(errorMessagesMap).some((error) =>
        errorMessage?.includes(error),
      )

      if (shouldShowModal) {
        const matchedError = Object.keys(errorMessagesMap).find((error) =>
          errorMessage?.includes(error),
        )
        const userFriendlyMessage = matchedError
          ? errorMessagesMap[matchedError]
          : 'Sorry, something went wrong while linking accounts. Please try again later.'
        setDisplayErrorMessage(userFriendlyMessage)
        setShowLinkingErrorModal(true)
      } else {
        // generic error message
        setDisplayErrorMessage(
          'Sorry, something went wrong while linking accounts. Please try again later.',
        )
        setShowLinkingErrorModal(true)
      }
    }
  }, [linkStatus, errorMessage, logout])

  useEffect(() => {
    if (isAuthenticated) {
      setIsLoading(true)
      getAccessTokenSilently()
        .then(setToken)
        .catch(() => setToken(null))
        .finally(() => setIsLoading(false))
    } else {
      setIsLoading(false)
    }
  }, [isAuthenticated])

  // Proactively refresh stored token before expiry (45 min)
  useEffect(() => {
    if (!isAuthenticated) return
    const REFRESH_INTERVAL = 45 * 60 * 1000
    const intervalId = setInterval(() => {
      getAccessTokenSilently()
        .then(setToken)
        .catch(() => {}) // queryFn will get its own fresh token
    }, REFRESH_INTERVAL)
    return () => clearInterval(intervalId)
  }, [isAuthenticated, getAccessTokenSilently])

  const getToken = useCallback(
    () => getAccessTokenSilently(),
    [getAccessTokenSilently],
  )

  const {
    data: currentUser,
    userData,
    isLoading: userLoading,
    isError: userError,
    updateUser,
    updateUserTermsAccepted,
    is409Error,
  } = useCurrentUser(token, logOutAndClear)

  const {
    orgs,
    isLoading: orgLoading,
    org_error: orgError,
  } = useUserOrgs(token, currentUser?.data as UserAPIResponse | null)

  const fullDataLoading = isLoading || orgLoading || userLoading || authLoading

  const error = userError || orgError

  const [switchError, setSwitchError] = useState<string | null>(null)

  const [localTermsAccepted, setLocalTermsAccepted] = useState(false)
  const hasAgreedToTerms =
    localTermsAccepted || currentUser?.data?.terms_accepted || false
  const canDismiss = currentUser?.data?.days_left_to_accept
    ? currentUser?.data?.days_left_to_accept !== 0
    : false

  // T&C banner: only show after user has a name (name gate collects it first)
  const hasName = !!(userData?.data?.first_name && userData?.data?.last_name)

  useEffect(() => {
    if (
      !fullDataLoading &&
      isAuthenticated &&
      currentUser?.data &&
      !hasAgreedToTerms &&
      hasName
    ) {
      setShowTermsModal(true)
    }
  }, [
    fullDataLoading,
    isAuthenticated,
    currentUser?.data,
    hasAgreedToTerms,
    hasName,
  ])

  function acceptTerms() {
    setShowTermsModal(false)
    setLocalTermsAccepted(true)
    if (currentUser?.data && token && currentUser.data.user_uuid) {
      updateUserTermsAccepted.mutate({
        token,
        userUuid: currentUser.data.user_uuid,
      })
    }
  }

  function closeTermsModal() {
    setShowTermsModal(false)
  }

  return (
    <UserContext.Provider
      value={{
        user: userData?.data ?? null,
        token,
        orgs,
        isError: !!error,
        isLoading: fullDataLoading,
        hasAgreedToTerms,
        acceptTerms,
        getToken,
        updateUser,
        switchError,
        setSwitchError,
      }}
    >
      <>
        {is409Error ? (
          <LoginConflictMessage />
        ) : (
          <>
            {children}

            {showTermsModal && !window.location.pathname.startsWith('/onboarding') && (
              <TermsAndConditionsBanner
                open={showTermsModal}
                onAgree={acceptTerms}
              />
            )}
            {showLinkingErrorModal && (
              <AccountLinkingErrorModal
                isOpen={showLinkingErrorModal}
                onClose={() => setShowLinkingErrorModal(false)}
                errorMessage={displayErrorMessage}
                userEmail={auth0User?.email || 'unknown'}
                onLogout={() => logOutAndClear({ returnTo: window.location.origin })}
              />
            )}
            {showLinkingSuccessModal && (
              <AccountLinkingSuccessModal
                isOpen={showLinkingSuccessModal}
                onComplete={completeAccountLinking}
              />
            )}

            {/* Rendered here, not in SelectOrg, so it's visible even if the
                switch error lands after the mobile nav (and SelectOrg with
                it) has already been closed. */}
            <Snackbar
              open={switchError !== null}
              autoHideDuration={6000}
              onClose={(_, reason) => {
                if (reason === 'clickaway') return
                setSwitchError(null)
              }}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
              aria-label="Organisation switch error notification"
            >
              <Alert
                severity="error"
                onClose={() => setSwitchError(null)}
                sx={{ width: '100%' }}
              >
                {switchError}
              </Alert>
            </Snackbar>
          </>
        )}
      </>
    </UserContext.Provider>
  )
}

export const getCurrentUser = () => useContext(UserContext)
